import { getLayerDefinition } from '../../layers/definitions';
import { LayerDescriptionModal, resolveCatalogLayerItem } from './LayerDescriptionModal';

const STORAGE_SEEN_KEY = 'gw_layer_intro_seen_v1';
const STORAGE_DISABLED_KEY = 'gw_layer_intro_disabled';

const CURATED_ONE_SENTENCE_DESCRIPTIONS: Record<string, { title: string; icon: string; subtitle: string; text: string }> = {
  sunrise_nowcast: {
    title: 'SkySkout Sunset & Sunrise NowCast',
    icon: '⚡',
    subtitle: 'LIVE SATELLITE & DOPPLER RADAR NOWCAST (T+2H / 4H / 6H)',
    text: 'Projects live GOES-18/19 multi-band cloud decks and MRMS Doppler radar forward by 2, 4, and 6 hours using optical-flow advection to score the very next twilight event based on actual observed conditions.'
  },
  sunrise_forecast: {
    title: 'Sunrise & Sunset 10-Day Forecast',
    icon: '🌅',
    subtitle: 'MULTI-MODEL NWP ATMOSPHERIC FORECAST',
    text: 'Predicts sunrise and sunset color vibrancy up to 10 days ahead by ray-tracing numerical weather prediction (NWP) cloud ceilings, horizon gaps, and aerosols along the solar azimuth.'
  },
  alpenglow_nowcast: {
    title: 'Golden Hour & Alpenglow Live NowCast',
    icon: '⚡',
    subtitle: 'LIVE SATELLITE & DOPPLER RADAR NOWCAST (T+2H / 4H / 6H)',
    text: 'Uses live GOES satellite imagery and radar optical flow (T+2h/4h/6h) to score low-angle golden hour light transmission and peak alpenglow for the next upcoming twilight event.'
  },
  alpenglow_forecast: {
    title: 'Golden Hour & Alpenglow 10-Day Forecast',
    icon: '🏔️',
    subtitle: 'MULTI-MODEL NWP ATMOSPHERIC FORECAST',
    text: 'Models low-angle direct solar beam transmission and topographic mountain peak illumination up to 10 days out using multi-model NWP cloud and moisture forecasts.'
  },
  darkskies: {
    title: 'Dark Skies Stargazing Forecast',
    icon: '🌌',
    subtitle: 'NIGHT SKY TRANSPARENCY & BORTLE INDEX',
    text: 'Combines total cloud cover, lunar illumination, aerosol optical depth, and artificial light pollution into a unified 0–100 stargazing and astrophotography quality score.'
  },
  clouds: {
    title: 'Cloud Cover Forecast',
    icon: '☁️',
    subtitle: 'HIGH, MID, LOW & TOTAL CLOUD DECKS',
    text: 'Visualizes forecasted high, mid, and low altitude cloud fractions across multiple atmospheric models to pinpoint clear windows and dramatic sky textures.'
  },
  valley_fog: {
    title: 'Valley Fog & Radiation Fog Probability',
    icon: '🌫️',
    subtitle: 'TERRAIN-TRAPPED BOUNDARY LAYER FOG',
    text: 'Identifies nocturnal temperature inversions, boundary-layer saturation, and topographic cold-air drainage to forecast low-lying valley fog and sea-of-clouds events.'
  },
  cloud_inversion: {
    title: 'Sea of Clouds (Cloud Inversion)',
    icon: '🏔️',
    subtitle: 'UNDERCAST TOPOGRAPHIC CLOUD TOPS',
    text: 'Highlights high-elevation viewpoints and mountain ridges that rise above a trapped stratus cloud deck for dramatic above-the-clouds vistas.'
  },
  lenticular: {
    title: 'Lenticular Wave Cloud Forecast',
    icon: '🛸',
    subtitle: 'OROGRAPHIC STANDING MOUNTAIN WAVES',
    text: 'Detects strong cross-barrier ridge winds combined with stable, moist mid-tropospheric layers capable of forming standing lenticular wave clouds downwind of peaks.'
  },
  hrtemp: {
    title: 'Terrain-Corrected Surface Temperature',
    icon: '🌡️',
    subtitle: 'HIGH-RESOLUTION LAPSE-RATE ADJUSTED TEMPS',
    text: 'Downscales numerical weather model temperatures onto high-resolution terrain elevation so mountain peaks and valley floors reflect true altitude-adjusted temperatures.'
  },
  winds: {
    title: 'Surface Winds & Gusts',
    icon: '💨',
    subtitle: '10-METER SUSTAINED WINDS & GUST VECTORS',
    text: 'Displays animated 10-meter wind streamlines and speed/gust magnitudes across high-resolution regional and global forecast models.'
  },
  precip: {
    title: 'Precipitation Rate, Probability & Snow',
    icon: '🌧️',
    subtitle: 'HOURLY RATE, POP% & 24H ACCUMULATION',
    text: 'Tracks forecasted rain and snow intensity, precipitation probability (PoP%), and 24-hour liquid or snowfall totals across NWP models.'
  },
  lightning: {
    title: 'Lightning & Convective Hazard',
    icon: '⚡',
    subtitle: 'CONVECTIVE CAPE & FLASH DENSITY POTENTIAL',
    text: 'Quantifies thunderstorm instability and lightning strike hazard using convective available potential energy (CAPE) and updraft helicity.'
  },
  smoke: {
    title: 'Wildfire Smoke & Vertically Integrated PM2.5',
    icon: '🔥',
    subtitle: 'NEAR-SURFACE & COLUMN SMOKE PLUMES',
    text: 'Tracks wildfire smoke dispersion at the surface and aloft to assess air quality impacts, hazy golden hours, and visibility degradation.'
  },
  aurora: {
    title: 'Aurora Borealis Viewline & Kp Forecast',
    icon: '🌌',
    subtitle: 'NOAA SWPC GEOMAGNETIC STORM OUTLOOK',
    text: 'Maps the southern aurora visibility horizon and geomagnetic Kp-index probabilities driven by real-time solar wind telemetry.'
  },
  seeing: {
    title: 'Astronomical Seeing & Jet Stream Stability',
    icon: '🔭',
    subtitle: 'OPTICAL TURBULENCE & THERMAL SHEAR',
    text: 'Evaluates atmospheric optical stability for planetary imaging and high-magnification stargazing based on vertical wind shear and thermal gradients.'
  },
  optics_dew_risk: {
    title: 'Camera & Telescope Optics Dew Risk',
    icon: '💧',
    subtitle: 'RADIATIVE COOLING CONDENSATION HAZARD',
    text: 'Predicts the likelihood of condensation forming on exposed camera lenses and telescope optics based on dewpoint depression, wind speed, and clear-sky radiative cooling.'
  }
};

export class LayerFirstTimeToast {
  private static instance: LayerFirstTimeToast | null = null;
  private container: HTMLElement | null = null;
  private dismissTimer: number | null = null;
  private descModal: LayerDescriptionModal | null = null;

  public static getInstance(): LayerFirstTimeToast {
    if (!LayerFirstTimeToast.instance) {
      LayerFirstTimeToast.instance = new LayerFirstTimeToast();
    }
    return LayerFirstTimeToast.instance;
  }

  public setDescriptionModal(modal: LayerDescriptionModal): void {
    this.descModal = modal;
  }

  private isSeen(toastKey: string): boolean {
    try {
      if (localStorage.getItem(STORAGE_DISABLED_KEY) === 'true') return true;
      const raw = localStorage.getItem(STORAGE_SEEN_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return Boolean(parsed && parsed[toastKey]);
    } catch {
      return false;
    }
  }

  private markSeen(toastKey: string): void {
    try {
      const raw = localStorage.getItem(STORAGE_SEEN_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[toastKey] = true;
      localStorage.setItem(STORAGE_SEEN_KEY, JSON.stringify(parsed));
    } catch {
      // ignore storage errors
    }
  }

  private extractOneSentence(text: string): string {
    const clean = text.replace(/\s+/g, ' ').trim();
    if (!clean) return '';
    const match = clean.match(/^.*?[.!?](?:\s|$)/);
    if (match && match[0].length >= 25 && match[0].length <= 240) {
      return match[0].trim();
    }
    return clean.length > 220 ? clean.slice(0, 217).trim() + '...' : clean;
  }

  public showIfFirstTime(layerKey: string, model?: string, force: boolean = false): void {
    if (!layerKey) return;
    const normLayer = layerKey.toLowerCase();
    const normModel = (model || '').toLowerCase();

    // Determine distinct key so users see context for both 10-Day Forecast and Live NowCast
    let toastKey = normLayer;
    if (normLayer === 'sunrise' || normLayer === 'sunrise_forecast' || normLayer === 'golden_hour') {
      toastKey = normModel === 'gw_nowcast' ? 'sunrise_nowcast' : 'sunrise_forecast';
    } else if (normLayer === 'alpenglow' || normLayer === 'alpenglow_forecast' || normLayer === 'goldenhour') {
      toastKey = normModel === 'gw_nowcast' ? 'alpenglow_nowcast' : 'alpenglow_forecast';
    }

    if (!force && this.isSeen(toastKey)) {
      return;
    }

    const catItem = resolveCatalogLayerItem(normLayer) || resolveCatalogLayerItem(toastKey);
    const def =
      getLayerDefinition(normLayer) ||
      (catItem?.id ? getLayerDefinition(catItem.id) : undefined) ||
      (catItem?.product ? getLayerDefinition(String(catItem.product)) : undefined);
    const fallbackCurated = CURATED_ONE_SENTENCE_DESCRIPTIONS[toastKey] || CURATED_ONE_SENTENCE_DESCRIPTIONS[normLayer];
    const isNowcast = toastKey.endsWith('_nowcast') || normModel === 'gw_nowcast';

    let info: { title: string; icon: string; subtitle: string; text: string } | undefined;

    // 1. Prioritize editable fields from MASTER_LAYER_CATALOG (activity_configs.yaml)
    if (isNowcast && catItem) {
      const ncTitle = catItem.nowcastToastTitle || catItem.nowcast_toast_title;
      const ncSub = catItem.nowcastToastSubtitle || catItem.nowcast_toast_subtitle;
      const ncDesc = catItem.nowcastToastDescription || catItem.nowcast_toast_description;
      if (ncDesc || ncTitle) {
        info = {
          title: ncTitle || fallbackCurated?.title || `${catItem.label || normLayer} NowCast`,
          icon: '⚡',
          subtitle: ncSub || fallbackCurated?.subtitle || 'LIVE SATELLITE & DOPPLER RADAR NOWCAST (T+2H)',
          text: ncDesc || fallbackCurated?.text || ''
        };
      }
    }

    if (!info && catItem) {
      const tTitle = catItem.toastTitle || catItem.toast_title;
      const tSub = catItem.toastSubtitle || catItem.toast_subtitle;
      const tDesc = catItem.toastDescription || catItem.toast_description;
      if (tDesc || tTitle) {
        info = {
          title: tTitle || catItem.label || fallbackCurated?.title || def?.name || normLayer,
          icon: catItem.icon || fallbackCurated?.icon || def?.icon || '🗺️',
          subtitle: tSub || catItem.categoryLabel || catItem.category_label || fallbackCurated?.subtitle || def?.categoryLabel || 'LAYER OVERVIEW',
          text: tDesc || fallbackCurated?.text || this.extractOneSentence(catItem.description || catItem.longDescription || '')
        };
      }
    }

    // 2. Fallback to curated dictionary or 1-sentence extraction
    if (!info) {
      info = fallbackCurated;
    }

    if (!info || !info.text) {
      const rawDesc =
        catItem?.toastDescription ||
        catItem?.toast_description ||
        catItem?.description ||
        def?.description ||
        catItem?.longDescription ||
        catItem?.long_description ||
        '';
      const oneSentence = this.extractOneSentence(rawDesc);
      if (!oneSentence) return;

      info = {
        title: catItem?.label || def?.name || normLayer,
        icon: catItem?.icon || def?.icon || '🗺️',
        subtitle: catItem?.categoryLabel || catItem?.category_label || def?.categoryLabel || 'LAYER OVERVIEW',
        text: oneSentence
      };
    }

    const canonicalLayerId = catItem?.id || normLayer;
    this.markSeen(toastKey);
    this.renderToast(canonicalLayerId, toastKey, info);
  }

  private renderToast(layerKey: string, toastKey: string, info: { title: string; icon: string; subtitle: string; text: string }): void {
    if (this.dismissTimer) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }

    if (!this.container) {
      this.container = document.createElement('div');
      this.container.id = 'gw-layer-first-time-toast';
      this.container.style.cssText = `
        position: fixed;
        top: 66px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 4200;
        width: min(520px, calc(100vw - 24px));
        pointer-events: auto;
        transition: opacity 0.25s ease, transform 0.25s ease;
      `;
      document.body.appendChild(this.container);
    }

    const isNowcast = toastKey.endsWith('_nowcast');
    const accentColor = isNowcast ? '#f59e0b' : '#38bdf8';
    const borderBg = isNowcast ? 'rgba(245, 158, 11, 0.45)' : 'rgba(56, 189, 248, 0.38)';
    const pillBg = isNowcast ? 'rgba(245, 158, 11, 0.16)' : 'rgba(56, 189, 248, 0.14)';

    this.container.style.opacity = '1';
    this.container.style.display = 'block';
    this.container.innerHTML = `
      <div style="
        background: rgba(15, 23, 42, 0.94);
        backdrop-filter: blur(14px);
        -webkit-backdrop-filter: blur(14px);
        border: 1px solid ${borderBg};
        border-left: 4px solid ${accentColor};
        border-radius: 12px;
        padding: 12px 14px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55);
        color: #f8fafc;
        font-family: inherit;
      ">
        <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 10px;">
          <div style="display: flex; align-items: center; gap: 9px; min-width: 0;">
            <span style="font-size: 1.25rem; line-height: 1;">${info.icon}</span>
            <div style="min-width: 0;">
              <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                <span style="font-size: 0.84rem; font-weight: 700; color: #ffffff;">${info.title}</span>
                <span style="font-size: 0.62rem; font-weight: 800; letter-spacing: 0.04em; padding: 2px 6px; border-radius: 999px; background: ${pillBg}; color: ${accentColor}; border: 1px solid ${borderBg};">
                  ${info.subtitle}
                </span>
              </div>
            </div>
          </div>
          <button type="button" id="gw-toast-close-btn" title="Dismiss" style="
            background: transparent;
            border: none;
            color: #94a3b8;
            font-size: 1.05rem;
            line-height: 1;
            cursor: pointer;
            padding: 2px 4px;
          ">✕</button>
        </div>
        <div style="font-size: 0.78rem; line-height: 1.45; color: #cbd5e1; margin-top: 7px;">
          ${info.text}
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 9px; padding-top: 7px; border-top: 1px solid rgba(255, 255, 255, 0.08);">
          <button type="button" id="gw-toast-more-info-btn" style="
            background: transparent;
            border: none;
            color: ${accentColor};
            font-size: 0.72rem;
            font-weight: 600;
            cursor: pointer;
            padding: 0;
          ">ℹ️ Full Layer Details</button>
          <div style="display: flex; align-items: center; gap: 10px;">
            <button type="button" id="gw-toast-mute-btn" style="
              background: transparent;
              border: none;
              color: #64748b;
              font-size: 0.68rem;
              cursor: pointer;
              padding: 0;
            ">Don't show layer tips</button>
            <button type="button" id="gw-toast-got-it-btn" style="
              background: ${accentColor};
              color: #0f172a;
              border: none;
              border-radius: 6px;
              padding: 4px 10px;
              font-size: 0.72rem;
              font-weight: 700;
              cursor: pointer;
            ">Got it</button>
          </div>
        </div>
      </div>
    `;

    const closeBtn = this.container.querySelector('#gw-toast-close-btn');
    const gotItBtn = this.container.querySelector('#gw-toast-got-it-btn');
    const muteBtn = this.container.querySelector('#gw-toast-mute-btn');
    const moreBtn = this.container.querySelector('#gw-toast-more-info-btn');

    const hide = () => this.hide();
    closeBtn?.addEventListener('click', hide);
    gotItBtn?.addEventListener('click', hide);
    muteBtn?.addEventListener('click', () => {
      try {
        localStorage.setItem(STORAGE_DISABLED_KEY, 'true');
      } catch {
        // ignore
      }
      this.hide();
    });
    moreBtn?.addEventListener('click', () => {
      this.hide();
      if (this.descModal) {
        this.descModal.open(layerKey);
      }
    });

    this.dismissTimer = window.setTimeout(() => {
      this.hide();
    }, 9500);
  }

  public hide(): void {
    if (this.dismissTimer) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.container) {
      this.container.style.display = 'none';
      this.container.innerHTML = '';
    }
  }
}
